import axios from 'axios';
import api from './api';
import { sessionHeaders } from '../utils/session';

// Trailing slash stripped: every call below appends "/api/..." by hand, and a
// base of "https://api.teamlio.ai/" would produce "//api/...", which FastAPI 404s.
const TEAMLIO_AUTH_URL = (
  process.env.REACT_APP_TEAMLIO_AUTH_URL || 'http://localhost:8000'
).replace(/\/+$/, '');

export interface Organization {
  head_office_id: string;
  company_name: string;
  organization: string;
}

export interface GetOrganizationsResult {
  success: boolean;
  organizations?: Organization[];
}

export interface LoginPayload {
  email: string;
  password: string;
  head_office_id: string;
}

export interface LoginResult {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  user?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string | null;
    [k: string]: unknown;
  };
}

export async function getOrganizations(email: string): Promise<GetOrganizationsResult> {
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/get-organizations`,
    { email },
    { timeout: 15000 }
  );
  return data;
}

export async function login(payload: LoginPayload): Promise<LoginResult> {
  // TeamLio's /api/auth/login-select accepts JSON with email, password, head_office_id.
  // The X-Client-* headers are what let the backend claim this platform's
  // single-session slot; without them the login is treated as unidentified
  // and claims nothing.
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/login-select`,
    {
      email: payload.email,
      password: payload.password,
      head_office_id: payload.head_office_id,
    },
    { timeout: 15000, headers: await sessionHeaders() }
  );
  if (!data?.access_token) {
    throw new Error('No access token in login response');
  }
  return data;
}

export async function refresh(refreshToken: string): Promise<LoginResult> {
  const { data } = await axios.post(
    `${TEAMLIO_AUTH_URL}/api/auth/refresh`,
    { refresh_token: refreshToken },
    { timeout: 15000 }
  );
  return data;
}

export const register = async (
  email: string,
  password: string,
  role: string,
  first_name: string,
  last_name: string,
  company_name?: string,
) => {
  try {
    console.log("auth.ts: Making registration request for:", email, "with role:", role);
    const payload: Record<string, any> = {
      email,
      password,
      role,
      first_name,
      last_name,
    };
    const trimmedCompany = (company_name || "").trim();
    if (trimmedCompany) payload.company_name = trimmedCompany;

    const response = await api.post(`${TEAMLIO_AUTH_URL}/api/auth/register`, payload);
    console.log("auth.ts: Registration response data:", response.data);
    return response.data;
  } catch (error: any) {
    console.error('auth.ts: Registration error:', error);
    console.error('auth.ts: Error response:', error?.response?.data);
    throw new Error(error?.response?.data?.detail || error.message);
  }
};

export async function logout(): Promise<void> {
  // Release this device's single-session slot BEFORE clearing the stored
  // token below. /api/auth/logout needs the bearer token (attached by
  // api.ts's request interceptor) to know whose slot to free, and it writes
  // the audit `logout` row to session_events — once localStorage is cleared
  // there is no token left to authenticate the request with. This exact
  // ordering bug has already shipped once (this function used to only clear
  // storage and never called the endpoint at all) — do NOT move the network
  // call after the removeItem calls a second time. A failure here must never
  // block local sign-out.
  try {
    await api.post(`${TEAMLIO_AUTH_URL}/api/auth/logout`);
  } catch (error) {
    console.warn('auth.ts: logout slot release failed (signing out anyway)', error);
  }

  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  } catch {
    /* ignore */
  }
}
